/**
 * racers.ts — the racer pool: the player + up to SEVEN AI rivals (M14: the
 * field size is selectable — 3, 5 or 7 CPU). Identity here is name + colour;
 * start lanes and per-racer pace come from `tuning.racers` (the lane slots and
 * the per-field skill spread — big fields get 2 aces and 2 backmarkers).
 *
 * Colours are chosen to stay distinguishable from each other and for the most
 * common colour blindness (no red/green pairs among neighbours on track).
 */
export interface RacerIdentity {
  id: number;
  name: string;
  color: number;
  isPlayer: boolean;
}

/** Player first, then rivals in the order they join bigger fields. */
export const RACER_POOL: RacerIdentity[] = [
  { id: 0, name: 'You', color: 0xff3b3b, isPlayer: true },
  { id: 1, name: 'Nova', color: 0x3b82f6, isPlayer: false },
  { id: 2, name: 'Rex', color: 0xffb43b, isPlayer: false },
  { id: 3, name: 'Jade', color: 0x2ad1a3, isPlayer: false },
  { id: 4, name: 'Blitz', color: 0xb07cff, isPlayer: false },
  { id: 5, name: 'Viper', color: 0xf2f4f6, isPlayer: false },
  { id: 6, name: 'Pip', color: 0xff7ab8, isPlayer: false },
  { id: 7, name: 'Momo', color: 0x8a9aa8, isPlayer: false },
];

/**
 * ONLINE display names (M44, pool rotation M52). Offline, slot 0 is the local
 * player ("You") — but online, slot 0 is just whoever connected first, so
 * showing "You" for it on EVERY client mislabels a stranger/friend. Online
 * screens map names through {@link onlineRacerName}: each client sees ITS OWN
 * slot as "You", and every other slot draws a handle from a big username pool,
 * picked DETERMINISTICALLY from the room code — every client in the room sees
 * the same names, every room rolls a different set, and no two slots in a room
 * can collide (the pool size is PRIME, so the stride walk below never repeats
 * inside a field of 8). Matchmade AI backfill drives normal slots, so it wears
 * these handles automatically — indistinguishable from a found human
 * (owner-locked: the backfill is never revealed).
 *
 * 73 handles (prime, ≥64 per the owner's rotation ask) — generic, global,
 * kid-safe, the way real quick-match lobbies read.
 */
export const ONLINE_NAME_POOL: readonly string[] = [
  'TurboTom', 'NitroNia', 'ball_hog_9', 'GoalMachine77', 'DriftQueen', 'PedalPete',
  'KeeperKai', 'SpeedyGonz', 'MightyMo', 'VoltVicky', 'xDriftKingx', 'La_Pulga10',
  'Rocketman_R', 'CurveCutter', 'SambaSam', 'TifoTina', 'BumperCarl', 'Nutmeg_Ned',
  'GasGasGas', 'OverstEER', 'ChicaneChad', 'HatTrickHana', 'ZoomZora', 'ApexAlba',
  'benzinho', 'WallriderW', 'Golazo_G', 'DerbyDee', 'PitstopPia', 'SlickRicky',
  'MidfieldMax', 'corner_kick', 'TurboTaxi', 'FreeKickFred', 'RedCardRex', 'VamosV',
  'HairpinHugo', 'StrikerSol', 'BoostedBea', 'OffsideOllie', 'DonutDan', 'KartKele',
  'panna_pro', 'FullThrottleF', 'CleanSheetCy', 'RumbleRosa', 'JetJaja', 'TorqueTia',
  'BicycleKickBo', 'LateBrakerL', 'MangoMachine', 'ZigzagZane', 'CalcioCarlo', 'TokyoTails',
  'DR1FTW00D', 'PenaltyPam', 'WheelieWes', 'number10shirt', 'RainRacer', 'TackleTeo',
  'GloryGlory', 'ovrtake_ova', 'CampeonCata', 'SundayLeague', 'VVroomVal', 'HalfVolleyHal',
  'NitroNoodle', 'BackPostBen', 'QuickSixQi', 'RampRunnerRo', 'StoppageTime', 'EasyEzy',
  'LastLapLena',
];

/** Tiny FNV-1a — stable across clients, good spread for short room codes. */
function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function onlineRacerName(slot: number, localSlot: number | null, roomKey = ''): string {
  if (slot === localSlot) return 'You';
  const n = ONLINE_NAME_POOL.length; // prime — any stride 1..n-1 walks all slots
  const base = hashStr(roomKey);
  const stride = (hashStr(`${roomKey}#stride`) % (n - 1)) + 1;
  return ONLINE_NAME_POOL[(base + slot * stride) % n];
}

/**
 * Vehicle assignment (M51 — vehicles are now REAL in the sim, in every mode).
 * The roster cycling here is the sim-side default (offline rivals + a fresh
 * online lobby before the room broadcasts real choices): slot 0 keeps the
 * hero sports car, rivals cycle for variety. Config-side (not entities/) so
 * the Cloudflare worker can import it without pulling in three.js/vite code.
 */
// NOTE: 'van' is the MONSTER TRUCK since M56 — the id is wire protocol
// (codec byte mapping + ?veh= URLs), so it never got renamed.
// M62: 'coupe' (the starter) is APPENDED last — VEHICLE_ROSTER is the positional
// wire-codec order (src/net/codec.ts) + the id allow-list, so new ids append
// only (byte 5 = coupe; bytes 0-4 unchanged). Bump SIM_VERSION when adding one.
// M114: 'f1' (the ultimate final unlock) APPENDED — byte 6; SIM_VERSION bumped
// with it (its stat profile is real sim behaviour clients predict).
export const VEHICLE_ROSTER = ['sports', 'muscle', 'pickup', 'van', 'motorcycle', 'coupe', 'f1'] as const;

// M62: the AI/rival cycling pool — the EARNABLE mid-ladder cars, NOT the
// starter coupe. Kept separate from VEHICLE_ROSTER so (a) rivals always show
// the cars the player is unlocking (the coupe is player-only), and (b) offline
// big-field rival assignment + online AI backfill stay byte-identical to
// pre-M62 (this list = the old VEHICLE_ROSTER verbatim). The coupe is still a
// valid, selectable, wire-encodable id via VEHICLE_ROSTER above.
// M114: 'f1' is PLAYER-ONLY too (like the coupe, deliberately excluded): the
// 1000 SP flex stays exclusive — an AI never spawns in it — and AI pace +
// matchmade-backfill vehicle distributions stay byte-identical to pre-M114.
export const AI_VEHICLE_POOL = ['sports', 'muscle', 'pickup', 'van', 'motorcycle'] as const;

export function defaultVehicleForSlot(slot: number): string {
  if (slot === 0) return 'sports';
  return AI_VEHICLE_POOL[slot % AI_VEHICLE_POOL.length];
}

/** Sanitize a wire-provided vehicle id (join URLs are user-controlled). */
export function asVehicleId(v: unknown): string {
  return typeof v === 'string' && (VEHICLE_ROSTER as readonly string[]).includes(v) ? v : 'sports';
}

/**
 * M62 — vehicle UNLOCK ladder. Every player starts with only the 'coupe'; the
 * earnable cars unlock as lifetime race points cross these thresholds (see
 * skillPoints.applySkillAward / isVehicleUnlocked). This is THE progression
 * tuning knob — order + point costs. Config-layer (no three.js / SaveData deps)
 * so both SaveData and Progression import it cycle-free.
 */
// MUST stay ASCENDING by points — nextUnlock() walks it in order to find the
// nearest next unlock. Order here also drives the garage card order (via
// VEHICLE_ORDER, kept in sync). Owner-tuned 2026-08-03 (M114: "increase the
// amount of SP for each sequential car" — the first unlock stays the cheap
// 30 SP taste, then the ladder climbs by 100s to the 1000 SP 'f1' crown).
// RAISING a threshold can NEVER re-lock an earned car: saves carry a
// granted-forever `unlockedVehicles` set (see SaveData parseSave + the
// LEGACY table below) that vehicleUnlockedBy() honours alongside points.
export const VEHICLE_UNLOCKS: readonly { id: string; points: number }[] = [
  { id: 'pickup', points: 100 },
  { id: 'muscle', points: 300 },
  { id: 'sports', points: 400 },
  { id: 'van', points: 500 },
  { id: 'motorcycle', points: 600 },
  { id: 'f1', points: 1000 },
];

/**
 * The PRE-M114 ladder, verbatim (owner-tuned 2026-07-09). Exists ONLY for the
 * SaveData grandfather migration: a pre-M114 blob (no `unlockedVehicles`
 * field) seeds its granted set from every vehicle THIS table says its points
 * had already earned, so the M114 threshold raise never strips a car a player
 * was already driving. Never consult it for live unlock decisions.
 */
export const LEGACY_VEHICLE_UNLOCKS_M62: readonly { id: string; points: number }[] = [
  { id: 'pickup', points: 30 },
  { id: 'muscle', points: 70 },
  { id: 'sports', points: 140 },
  { id: 'van', points: 200 },
  { id: 'motorcycle', points: 275 },
];

/** Lifetime-points cost to unlock a vehicle. The starter coupe is always free
 *  (not in the table → 0); an unknown id also returns 0 (fail-open — a typo'd
 *  or future id can never soft-brick a selection). */
export function unlockThresholdFor(id: string): number {
  return VEHICLE_UNLOCKS.find((u) => u.id === id)?.points ?? 0;
}

/** The pre-M114 cost of a vehicle (0 when it wasn't in the old ladder) — the
 *  grandfather-migration mirror of {@link unlockThresholdFor}. */
export function legacyUnlockThresholdFor(id: string): number {
  return LEGACY_VEHICLE_UNLOCKS_M62.find((u) => u.id === id)?.points ?? 0;
}

/**
 * M114 — THE one unlock predicate, shared by Progression.isVehicleUnlocked and
 * the SaveData load-boundary ride coercion (config-layer + pure so neither
 * import cycles). A vehicle is available when it's the free starter, when
 * lifetime points reach its CURRENT threshold, or when it's in the save's
 * granted-forever `unlockedVehicles` set (a past crossing / the legacy
 * migration) — so a threshold retune can never re-lock an earned car.
 * Unknown ids fail open via unlockThresholdFor → 0 (never soft-brick a pick).
 */
export function vehicleUnlockedBy(points: number, granted: readonly string[], id: string): boolean {
  if (id === 'coupe') return true;
  return points >= unlockThresholdFor(id) || granted.includes(id);
}

/** M87 (owner rule): the offline field is FIXED — you + 6 CPU rivals, 7 on
 *  course, in every offline mode. The old selectable 3/5/7 chips are gone;
 *  online field size is separate room config and unaffected. */
export const OFFLINE_RIVALS = 6;
